import type { RegexPattern } from '../types';

/**
 * @group Constants
 * @summary <blockquote>Regex patterns for URL matching and extraction.</blockquote>
 * 
 * Patterns used for matching URLs in text, extracting URLs from markdown, and cleaning URL strings.
 */
export const URL_PATTERNS = {
  /**
   * Matches HTTP and HTTPS URLs in text
   * Stops at common URL-ending characters (spaces, parentheses, brackets, quotes, etc.)
   * Example: Used to extract URLs from text content
   */
  HTTP_URL: {
    regex: /https?:\/\/[^\s\)\]\>\"\']+/g,
    description: 'Matches HTTP and HTTPS URLs in text, stopping at common URL-ending characters',
    examples: [
      'https://example.com',
      'http://example.com/path',
      'https://example.com/page?query=value',
      'https://example.com#anchor',
    ],
    counterExamples: [
      'example.com',
      'ftp://example.com',
      'https://example.com)',
      'https://example.com ',
    ],
  } as RegexPattern,

  /**
   * Matches trailing punctuation characters (periods, commas, semicolons, colons, exclamation marks, question marks)
   * Example: Used to clean URLs that have trailing punctuation
   */
  TRAILING_PUNCTUATION: {
    regex: /[.,;:!?]+$/,
    description: 'Matches trailing punctuation characters at the end of a string',
    examples: ['text.', 'text,', 'text;', 'text:', 'text!', 'text?', 'text...'],
    counterExamples: ['text', '.text', 'text, more', 'text. more'],
  } as RegexPattern,

  /**
   * Matches markdown link format [text](url)
   * Captures the link text in group 1
   * Example: Used to extract link text from markdown links
   */
  MARKDOWN_LINK: {
    regex: /\[([^\]]+)\]\(([^)]+)\)/g,
    description: 'Matches markdown link format [text](url), capturing text in group 1 and URL in group 2',
    examples: [
      '[Example](https://example.com)',
      '[Link Text](http://example.com/path)',
      '[Google](https://google.com)',
    ],
    counterExamples: [
      'https://example.com',
      '[Example]',
      '(https://example.com)',
      '[Example](url with spaces)',
    ],
  } as RegexPattern,
} as const;
